import React, { forwardRef } from 'react';
import type { NewSizingSnapshot, NewSizingRequest } from '@/sizing/newSizingEngine';
import type { SizingNarrativeData } from '@/types/narrative';
import { format } from 'date-fns';

interface SizingPdfReportProps {
  snapshot: NewSizingSnapshot;
  request: NewSizingRequest;
  narrativeData: SizingNarrativeData | null;
  serverDisplayName?: string;
  acceleratorDisplayName?: string;
}

const BYTES_PER_GIB = 1024 ** 3;

const formatNumber = (n: number, decimals = 0): string => {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatDecimal = (n: number, decimals = 2): string => {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
};

// Section Header Component
const SectionHeader = ({ number, title, subtitle }: { number: string; title: string; subtitle?: string }) => (
  <div className="mt-8 mb-4 pb-2 border-b-2 border-blue-600">
    <div className="flex items-baseline gap-3">
      <span className="text-xl font-bold text-blue-600">{number}</span>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

// Step Header Component
const StepHeader = ({ step, title }: { step: number; title: string }) => (
  <div className="flex items-center gap-2 mt-6 mb-3">
    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">Step {step}</span>
    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
  </div>
);

// Formula Block Component
const FormulaBlock = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 font-mono text-sm my-3">
    {children}
  </div>
);

// Metric Card Component
const MetricCard = ({ label, value, sublabel, highlight }: { label: string; value: string | number; sublabel?: string; highlight?: boolean }) => (
  <div className={`border rounded-lg p-3 text-center ${highlight ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-xl font-bold ${highlight ? 'text-blue-600' : 'text-gray-900'}`}>{value}</p>
    {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
  </div>
);

export const SizingPdfReport = forwardRef<HTMLDivElement, SizingPdfReportProps>(
  ({ snapshot, request, narrativeData, serverDisplayName = 'Dell XE7740', acceleratorDisplayName = 'Intel Gaudi 3' }, ref) => {
    const serverCount = Math.ceil(snapshot.finalGpuCount / 8);
    const gpuCount = snapshot.finalGpuCount;
    const timestamp = format(new Date(), 'MMMM d, yyyy h:mm a');

    const bottleneckLabel = {
      kv: 'KV Memory',
      prompt: 'Prompt Throughput',
      decode: 'Decode Throughput',
      none: 'None',
    }[snapshot.finalBottleneck];

    const hbmGiB = request.model.hbmGiB ?? 96;
    const weightsGiB = request.model.weightsGiB ?? 0;
    const weightsUnshared = request.model.weightsGiBUnshared ?? weightsGiB;
    const runtimeGiB = hbmGiB - weightsGiB - (request.model.usableKvGiBPerGpu ?? 0);
    const usableKvGiB = request.model.usableKvGiBPerGpu ?? 0;
    const bridgeGroupSize = request.model.bridgeGroupSize ?? 1;

    // Calculate percentages for memory bar
    const weightsPct = (weightsGiB / hbmGiB) * 100;
    const runtimePct = (runtimeGiB / hbmGiB) * 100;
    const kvPct = (usableKvGiB / hbmGiB) * 100;

    return (
      <div
        ref={ref}
        className="bg-white text-gray-900 p-10"
        style={{ width: '794px', fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        {/* ===== COVER PAGE ===== */}
        <div className="min-h-[700px] flex flex-col">
          {/* Header */}
          <div className="border-b-4 border-blue-600 pb-4 mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1">
                  AI Infrastructure Sizing Report
                </h1>
                <p className="text-lg text-gray-600">Technical White Paper</p>
                <p className="text-sm text-gray-400 mt-2">Generated {timestamp}</p>
              </div>
              <div className="text-right">
                <div className="text-xl font-semibold text-blue-600">Dell | Intel</div>
                <div className="text-xs text-gray-400 mt-1">{serverDisplayName}</div>
                <div className="text-xs text-gray-400">{acceleratorDisplayName}</div>
              </div>
            </div>
          </div>

          {/* Executive Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 border border-blue-100">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Executive Summary</h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">{serverCount}</div>
                <div className="text-xs text-gray-600 mt-1">Servers</div>
              </div>
              <div className="text-center border-l border-blue-200 pl-4">
                <div className="text-4xl font-bold text-indigo-600">{gpuCount}</div>
                <div className="text-xs text-gray-600 mt-1">Total GPUs</div>
              </div>
              <div className="text-center border-l border-blue-200 pl-4">
                <div className="text-4xl font-bold text-gray-700">{formatNumber(request.baseConcurrentUsers)}</div>
                <div className="text-xs text-gray-600 mt-1">Concurrent Users</div>
              </div>
              <div className="text-center border-l border-blue-200 pl-4">
                <div className="text-lg font-bold text-gray-700 mt-2">{bottleneckLabel}</div>
                <div className="text-xs text-gray-600 mt-1">Limiting Constraint</div>
              </div>
            </div>
          </div>

          {/* Configuration Summary */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">
                Model Configuration
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-1.5 text-gray-500">Model</td>
                    <td className="py-1.5 text-right font-medium">{request.model.id}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-1.5 text-gray-500">Parameters</td>
                    <td className="py-1.5 text-right font-medium">{request.model.paramsBillions}B</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-1.5 text-gray-500">Precision</td>
                    <td className="py-1.5 text-right font-medium">{request.model.precisionBits}-bit</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-500">Batch Size</td>
                    <td className="py-1.5 text-right font-medium">{request.batchSize}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">
                Workload Configuration
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-1.5 text-gray-500">Concurrent Users</td>
                    <td className="py-1.5 text-right font-medium">{formatNumber(request.baseConcurrentUsers)}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-1.5 text-gray-500">Conversation Turns</td>
                    <td className="py-1.5 text-right font-medium">{request.conversationTurns}</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-1.5 text-gray-500">Latency Target</td>
                    <td className="py-1.5 text-right font-medium">{formatNumber(request.latencySloMs)} ms</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 text-gray-500">User Personas</td>
                    <td className="py-1.5 text-right font-medium">{request.personas.length}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="border border-gray-200 rounded-lg p-4 mt-auto">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Contents</h3>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div>1. Methodology Overview</div>
              <div>4. Memory Sizing</div>
              <div>2. User Persona Analysis</div>
              <div>5. Bottleneck Analysis</div>
              <div>3. Throughput Calculations</div>
              <div>6. Final Recommendations</div>
            </div>
          </div>
        </div>

        {/* ===== SECTION 1: METHODOLOGY ===== */}
        <div style={{ pageBreakBefore: 'always' }}>
          <SectionHeader 
            number="1" 
            title="Methodology Overview" 
            subtitle="Monte Carlo simulation approach for realistic capacity planning"
          />

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Monte Carlo Simulation</h4>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              Our sizing methodology uses Monte Carlo simulation to model realistic user behavior patterns.
              Rather than assuming all users are active simultaneously, we simulate individual users following
              probabilistic state machines with read, think, and query phases.
            </p>
            <div className="grid grid-cols-3 gap-4 text-center mt-4">
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">{snapshot.trials}</div>
                <div className="text-xs text-gray-500">Simulation Trials</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">{snapshot.horizonSec / 60} min</div>
                <div className="text-xs text-gray-500">Simulation Horizon</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-2xl font-bold text-blue-600">P95</div>
                <div className="text-xs text-gray-500">Sizing Percentile</div>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-800 mb-2">User State Machine</h4>
          <p className="text-sm text-gray-600 mb-3">
            Each user cycles through three states with exponentially distributed durations:
          </p>
          <div className="flex items-center justify-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center px-4 py-2 bg-blue-100 rounded-lg border border-blue-300">
              <div className="font-semibold text-blue-800">Reading</div>
              <div className="text-xs text-blue-600">Digest response</div>
            </div>
            <div className="text-xl text-gray-400">→</div>
            <div className="text-center px-4 py-2 bg-purple-100 rounded-lg border border-purple-300">
              <div className="font-semibold text-purple-800">Thinking</div>
              <div className="text-xs text-purple-600">Formulate query</div>
            </div>
            <div className="text-xl text-gray-400">→</div>
            <div className="text-center px-4 py-2 bg-green-100 rounded-lg border border-green-300">
              <div className="font-semibold text-green-800">Generating</div>
              <div className="text-xs text-green-600">AI inference</div>
            </div>
            <div className="text-xl text-gray-400">→</div>
            <div className="text-xs text-gray-400">(repeat)</div>
          </div>

          <FormulaBlock>
            <div className="text-gray-500 text-xs mb-1">Arrival Rate (Poisson Process):</div>
            <div>λ = targetConcurrent / (readTime + thinkTime + serviceTime)</div>
          </FormulaBlock>

          <h4 className="font-semibold text-gray-800 mb-2 mt-6">Why P95 Sizing?</h4>
          <p className="text-sm text-gray-600 leading-relaxed">
            We size infrastructure for the <strong>95th percentile</strong> of concurrent demand. This ensures:
          </p>
          <ul className="text-sm text-gray-600 list-disc ml-6 mt-2 space-y-1">
            <li>95% of the time, the system has adequate capacity without degradation</li>
            <li>Avoids costly over-provisioning for rare P99+ outliers</li>
            <li>Balances cost efficiency with service level objectives</li>
          </ul>
        </div>

        {/* ===== SECTION 2: USER PERSONAS ===== */}
        <div style={{ pageBreakBefore: 'always' }}>
          <SectionHeader 
            number="2" 
            title="User Persona Analysis" 
            subtitle="Characterizing user segments with unique usage patterns"
          />

          <StepHeader step={1} title="Persona Definition" />
          <FormulaBlock>
            <div className="text-gray-500 text-xs mb-1">Population calculation:</div>
            <div>population = totalUsers × (sharePercent / 100)</div>
          </FormulaBlock>

          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden mb-6">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Persona</th>
                <th className="text-right py-2 px-3 font-semibold text-gray-700">Share</th>
                <th className="text-right py-2 px-3 font-semibold text-gray-700">Population</th>
                <th className="text-right py-2 px-3 font-semibold text-gray-700">Input Tok</th>
                <th className="text-right py-2 px-3 font-semibold text-gray-700">Output Tok</th>
                <th className="text-right py-2 px-3 font-semibold text-gray-700">Read (s)</th>
                <th className="text-right py-2 px-3 font-semibold text-gray-700">Think (s)</th>
              </tr>
            </thead>
            <tbody>
              {request.personas.map((persona, idx) => (
                <tr key={persona.id} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                  <td className="py-2 px-3 font-medium">{persona.name}</td>
                  <td className="py-2 px-3 text-right">{formatDecimal(persona.sharePercent, 1)}%</td>
                  <td className="py-2 px-3 text-right">{formatNumber(persona.population)}</td>
                  <td className="py-2 px-3 text-right">{formatNumber(persona.inputTokensPerConversation)}</td>
                  <td className="py-2 px-3 text-right">{formatNumber(persona.outputTokensPerConversation)}</td>
                  <td className="py-2 px-3 text-right">{formatDecimal(persona.readSeconds, 0)}</td>
                  <td className="py-2 px-3 text-right">{formatDecimal(persona.thinkSeconds, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <StepHeader step={2} title="Monte Carlo Session Distribution" />
          <FormulaBlock>
            <div>cycleSec = readTime + thinkTime + serviceTime</div>
            <div className="mt-1">activeRatio = serviceTime / cycleSec</div>
            <div className="mt-1">expectedActive = population × activeRatio</div>
          </FormulaBlock>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <MetricCard label="P50 Active Sessions" value={formatDecimal(snapshot.p50Active, 1)} sublabel="Median concurrent load" />
            <MetricCard label="P95 Active Sessions" value={formatDecimal(snapshot.p95Active, 1)} sublabel="Sizing target (95th percentile)" highlight />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-gray-700 text-sm mb-2">Percentile Distribution</h4>
            <div className="grid grid-cols-5 gap-2 text-center">
              {([5, 25, 50, 75, 95] as const).map((p) => (
                <div key={p} className={`rounded p-2 ${p === 95 ? 'bg-blue-100 border border-blue-300' : 'bg-white border border-gray-200'}`}>
                  <div className="text-xs text-gray-500">P{p}</div>
                  <div className={`font-bold ${p === 95 ? 'text-blue-600' : 'text-gray-700'}`}>
                    {formatDecimal(snapshot.activeByPercentile[p], 1)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===== SECTION 3: THROUGHPUT ===== */}
        <div style={{ pageBreakBefore: 'always' }}>
          <SectionHeader 
            number="3" 
            title="Throughput Calculations" 
            subtitle="Token processing capacity requirements"
          />

          <StepHeader step={3} title="Required Throughput" />
          <p className="text-sm text-gray-600 mb-3">
            Based on Monte Carlo simulation results, we calculate the tokens per second (TPS) required to serve P95 concurrent load:
          </p>

          <FormulaBlock>
            <div className="text-gray-500 text-xs mb-1">Prompt throughput (input tokens):</div>
            <div>promptTPS = avgPromptTokensPerSec / utilizationTarget</div>
            <div className="mt-2 text-gray-500 text-xs">Decode throughput (output tokens):</div>
            <div>decodeTPS = avgDecodeTokensPerSec / utilizationTarget</div>
          </FormulaBlock>

          <div className="grid grid-cols-2 gap-6 mt-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-700 mb-3">Prompt (Input) Throughput</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Required TPS:</span>
                  <span className="font-bold text-lg">{formatNumber(snapshot.requiredPromptTps)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Per-GPU TPS:</span>
                  <span className="font-medium">{formatNumber(snapshot.promptTpsPerGpu)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-500">GPUs Required:</span>
                  <span className="font-bold text-blue-600">{snapshot.promptGpus}</span>
                </div>
              </div>
              <FormulaBlock>
                <div className="text-xs">{formatNumber(snapshot.requiredPromptTps)} ÷ {formatNumber(snapshot.promptTpsPerGpu)} = {snapshot.promptGpus} GPUs</div>
              </FormulaBlock>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-700 mb-3">Decode (Output) Throughput</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Required TPS:</span>
                  <span className="font-bold text-lg">{formatNumber(snapshot.requiredDecodeTps)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Per-GPU TPS:</span>
                  <span className="font-medium">{formatNumber(snapshot.decodeTpsPerGpu)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-500">GPUs Required:</span>
                  <span className="font-bold text-blue-600">{snapshot.decodeGpus}</span>
                </div>
              </div>
              <FormulaBlock>
                <div className="text-xs">{formatNumber(snapshot.requiredDecodeTps)} ÷ {formatNumber(snapshot.decodeTpsPerGpu)} = {snapshot.decodeGpus} GPUs</div>
              </FormulaBlock>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-yellow-800 text-sm mb-1">Throughput GPUs Required</h4>
            <p className="text-sm text-yellow-700">
              <strong>{Math.max(snapshot.promptGpus, snapshot.decodeGpus)} GPUs</strong> needed to satisfy throughput requirements
              (max of prompt and decode).
            </p>
          </div>
        </div>

        {/* ===== SECTION 4: MEMORY SIZING ===== */}
        <div style={{ pageBreakBefore: 'always' }}>
          <SectionHeader 
            number="4" 
            title="Memory Sizing" 
            subtitle="GPU memory allocation for model weights and KV cache"
          />

          <StepHeader step={4} title="GPU Memory Budget" />
          <p className="text-sm text-gray-600 mb-3">
            Each GPU's High Bandwidth Memory (HBM) must accommodate model weights, runtime overhead, and KV cache for active sessions:
          </p>

          {/* Memory bar visualization */}
          <div className="bg-gray-100 rounded-lg p-4 mb-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>GPU Memory Allocation</span>
              <span>{formatDecimal(hbmGiB, 0)} GiB Total</span>
            </div>
            <div className="h-8 bg-gray-300 rounded-lg overflow-hidden flex">
              <div 
                className="bg-orange-500 flex items-center justify-center text-[10px] text-white font-medium"
                style={{ width: `${weightsPct}%` }}
              >
                {formatDecimal(weightsGiB, 1)}
              </div>
              <div 
                className="bg-yellow-500 flex items-center justify-center text-[10px] text-white font-medium"
                style={{ width: `${runtimePct}%` }}
              >
                {formatDecimal(runtimeGiB, 1)}
              </div>
              <div 
                className="bg-blue-500 flex items-center justify-center text-[10px] text-white font-medium"
                style={{ width: `${kvPct}%` }}
              >
                {formatDecimal(usableKvGiB, 1)}
              </div>
            </div>
            <div className="flex gap-4 text-xs mt-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded" />
                <span>Weights ({formatDecimal(weightsPct, 0)}%)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-yellow-500 rounded" />
                <span>Runtime ({formatDecimal(runtimePct, 0)}%)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded" />
                <span>KV Cache ({formatDecimal(kvPct, 0)}%)</span>
              </div>
            </div>
          </div>

          <FormulaBlock>
            <div>usableKvPerGpu = hbmPerGpu - weightsPerGpu - runtimeOverhead</div>
            <div className="mt-1 text-gray-500 text-xs">
              = {formatDecimal(hbmGiB, 0)} - {formatDecimal(weightsGiB, 1)} - {formatDecimal(runtimeGiB, 1)} = {formatDecimal(usableKvGiB, 1)} GiB
            </div>
          </FormulaBlock>

          {/* Tensor Parallelism */}
          {bridgeGroupSize > 1 && (
            <>
              <h4 className="font-semibold text-gray-800 mb-2 mt-6">Tensor Parallelism (GPU Bridging)</h4>
              <p className="text-sm text-gray-600 mb-3">
                With Tensor Parallelism (TP={bridgeGroupSize}), model weights are distributed across {bridgeGroupSize} GPUs, 
                reducing per-GPU memory requirements:
              </p>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <MetricCard label="Tensor Parallel" value={`TP=${bridgeGroupSize}`} />
                <MetricCard label="Weights (TP={bridgeGroupSize})" value={`${formatDecimal(weightsGiB, 1)} GiB`} />
                <MetricCard label="Weights (TP=1)" value={`${formatDecimal(weightsUnshared, 1)} GiB`} />
                <MetricCard label="Memory Saved" value={`${formatDecimal(weightsUnshared - weightsGiB, 1)} GiB`} highlight />
              </div>
            </>
          )}

          {/* KV Cache Sizing */}
          <StepHeader step={5} title="KV Cache Memory Sizing" />
          <FormulaBlock>
            <div>kvBytesPerSession = tokensPerSession × kvBytesPerToken</div>
            <div className="mt-1">totalKvGiB = kvSessionsP95 × kvBytesPerSession / 1024³</div>
            <div className="mt-1">kvGpus = ceil(totalKvGiB / usableKvPerGpu)</div>
          </FormulaBlock>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="border border-gray-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">KV Bytes/Token:</span>
                <span className="font-medium">{formatNumber(snapshot.kvBytesPerToken)} bytes</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">P95 KV Sessions:</span>
                <span className="font-medium">{formatDecimal(snapshot.kvSessionsP95, 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total KV Memory:</span>
                <span className="font-medium">{formatDecimal(snapshot.kvGiBP95, 1)} GiB</span>
              </div>
            </div>
            <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
              <div className="text-sm text-gray-600 mb-1">KV Memory GPUs Required</div>
              <div className="text-3xl font-bold text-blue-600">{snapshot.kvGpusP95}</div>
              <div className="text-xs text-gray-500 mt-1">
                {formatDecimal(snapshot.kvGiBP95, 1)} GiB ÷ {formatDecimal(usableKvGiB, 1)} GiB/GPU
              </div>
            </div>
          </div>

          {/* Per-Persona KV Breakdown */}
          {narrativeData?.personaKvBreakdowns && narrativeData.personaKvBreakdowns.length > 0 && (
            <>
              <h4 className="font-semibold text-gray-800 mb-2 mt-6">Per-Persona KV Memory Allocation</h4>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Persona</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">P95 Sessions</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Tokens/Session</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">KV/Session</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Total KV</th>
                  </tr>
                </thead>
                <tbody>
                  {narrativeData.personaKvBreakdowns.map((breakdown, idx) => (
                    <tr key={breakdown.personaId} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                      <td className="py-2 px-3 font-medium">{breakdown.personaName}</td>
                      <td className="py-2 px-3 text-right">{formatDecimal(breakdown.sessionsP95, 0)}</td>
                      <td className="py-2 px-3 text-right">{formatNumber(breakdown.tokensPerSession)}</td>
                      <td className="py-2 px-3 text-right">{formatDecimal(breakdown.kvGiBPerSession, 2)} GiB</td>
                      <td className="py-2 px-3 text-right font-medium">{formatDecimal(breakdown.totalKvGiB, 1)} GiB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* ===== SECTION 5: BOTTLENECK ANALYSIS ===== */}
        <div style={{ pageBreakBefore: 'always' }}>
          <SectionHeader 
            number="5" 
            title="Bottleneck Analysis" 
            subtitle="Identifying the limiting constraint for infrastructure sizing"
          />

          <StepHeader step={6} title="Constraint Comparison" />
          <FormulaBlock>
            <div>finalGPUs = max(promptGPUs, decodeGPUs, kvGPUs)</div>
            <div className="mt-1 text-gray-500 text-xs">
              = max({snapshot.promptGpus}, {snapshot.decodeGpus}, {snapshot.kvGpusP95}) = {snapshot.finalGpuCount} GPUs
            </div>
          </FormulaBlock>

          {/* Constraint comparison bars */}
          <div className="space-y-4 mt-4">
            {[
              { label: 'Prompt Throughput', gpus: snapshot.promptGpus, isBottleneck: snapshot.finalBottleneck === 'prompt' },
              { label: 'Decode Throughput', gpus: snapshot.decodeGpus, isBottleneck: snapshot.finalBottleneck === 'decode' },
              { label: 'KV Memory', gpus: snapshot.kvGpusP95, isBottleneck: snapshot.finalBottleneck === 'kv' },
            ].map((constraint) => {
              const pct = gpuCount > 0 ? (constraint.gpus / gpuCount) * 100 : 0;
              return (
                <div key={constraint.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={constraint.isBottleneck ? 'font-semibold text-blue-600' : 'text-gray-600'}>
                      {constraint.label}
                      {constraint.isBottleneck && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          BOTTLENECK
                        </span>
                      )}
                    </span>
                    <span className="font-medium">{constraint.gpus} GPUs</span>
                  </div>
                  <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${constraint.isBottleneck ? 'bg-blue-500' : 'bg-gray-300'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottleneck explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <h4 className="font-semibold text-blue-800 text-sm mb-2">Analysis</h4>
            <p className="text-sm text-blue-700 leading-relaxed">
              Your infrastructure is primarily constrained by <strong>{bottleneckLabel}</strong>. 
              {snapshot.finalBottleneck === 'kv' && 
                ' The KV cache memory required to maintain session context for concurrent users exceeds throughput demands.'}
              {snapshot.finalBottleneck === 'prompt' && 
                ' Input token processing throughput is the limiting factor for your workload.'}
              {snapshot.finalBottleneck === 'decode' && 
                ' Output token generation throughput is the limiting factor for your workload.'}
            </p>
          </div>

          {/* Utilization analysis */}
          <h4 className="font-semibold text-gray-800 mb-2 mt-6">Resource Utilization at {gpuCount} GPUs</h4>
          <div className="grid grid-cols-3 gap-3">
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500 mb-1">Prompt Utilization</div>
              <div className="text-xl font-bold">
                {formatDecimal((snapshot.promptGpus / gpuCount) * 100, 0)}%
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500 mb-1">Decode Utilization</div>
              <div className="text-xl font-bold">
                {formatDecimal((snapshot.decodeGpus / gpuCount) * 100, 0)}%
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500 mb-1">KV Memory Utilization</div>
              <div className="text-xl font-bold">
                {formatDecimal((snapshot.kvGpusP95 / gpuCount) * 100, 0)}%
              </div>
            </div>
          </div>
        </div>

        {/* ===== SECTION 6: FINAL RECOMMENDATIONS ===== */}
        <div style={{ pageBreakBefore: 'always' }}>
          <SectionHeader 
            number="6" 
            title="Final Recommendations" 
            subtitle="Infrastructure deployment specifications"
          />

          <StepHeader step={7} title="Server Deployment" />
          <FormulaBlock>
            <div>servers = ceil(finalGPUs / gpusPerServer)</div>
            <div className="mt-1 text-gray-500 text-xs">
              = ceil({gpuCount} / 8) = {serverCount} servers
            </div>
          </FormulaBlock>

          {/* Final recommendation cards */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="border-2 border-blue-500 rounded-lg p-5 bg-gradient-to-br from-blue-50 to-indigo-50">
              <div className="text-sm text-gray-600 mb-1">Total GPUs Required</div>
              <div className="text-5xl font-bold text-blue-600">{gpuCount}</div>
              <div className="text-xs text-gray-500 mt-2">
                {acceleratorDisplayName} accelerators
              </div>
            </div>
            <div className="border-2 border-indigo-500 rounded-lg p-5 bg-gradient-to-br from-indigo-50 to-purple-50">
              <div className="text-sm text-gray-600 mb-1">Servers Needed</div>
              <div className="text-5xl font-bold text-indigo-600">{serverCount}</div>
              <div className="text-xs text-gray-500 mt-2">
                {serverDisplayName} (8 GPUs each)
              </div>
            </div>
          </div>

          {/* Capacity summary */}
          <div className="border border-gray-200 rounded-lg p-4 mt-6">
            <h4 className="font-semibold text-gray-700 mb-3">Capacity Summary</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Concurrent Users:</span>
                  <span className="font-medium">{formatNumber(request.baseConcurrentUsers)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Users per Server:</span>
                  <span className="font-medium">{formatDecimal(request.baseConcurrentUsers / serverCount, 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Active Sessions (P95):</span>
                  <span className="font-medium">{formatDecimal(snapshot.p95Active, 0)}</span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total HBM Capacity:</span>
                  <span className="font-medium">{formatNumber(gpuCount * hbmGiB)} GiB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">KV Cache Capacity:</span>
                  <span className="font-medium">{formatNumber(gpuCount * usableKvGiB)} GiB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Limiting Constraint:</span>
                  <span className="font-medium">{bottleneckLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Key insights */}
          <div className="bg-gray-50 rounded-lg p-4 mt-6">
            <h4 className="font-semibold text-gray-700 mb-3">Key Insights</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Infrastructure is sized for <strong>P95 load</strong> ({formatDecimal(snapshot.p95Active, 0)} concurrent active sessions),
                  ensuring adequate capacity 95% of the time.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  <strong>{bottleneckLabel}</strong> is the primary constraint. Consider optimizing this area for cost efficiency.
                </span>
              </li>
              {bridgeGroupSize > 1 && (
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>
                    Tensor Parallelism (TP={bridgeGroupSize}) saves <strong>{formatDecimal(weightsUnshared - weightsGiB, 1)} GiB</strong> per GPU 
                    by distributing model weights across GPUs.
                  </span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>
                  Monte Carlo simulation accounts for realistic user duty cycles, avoiding over-provisioning from naive 
                  "all users active simultaneously" assumptions.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="border-t-2 border-gray-200 pt-4 mt-8">
          <div className="flex justify-between items-center text-xs text-gray-400">
            <div>Dell AI Infrastructure Sizing Tool</div>
            <div>Report ID: {Date.now().toString(36).toUpperCase()}</div>
          </div>
          <p className="text-xs text-gray-400 mt-2 leading-relaxed">
            This report provides estimates based on Monte Carlo simulation models and should be validated against 
            actual workload characteristics. Actual performance may vary based on specific use cases, data patterns, 
            and system configuration. The sizing recommendations target P95 load conditions for balanced cost-efficiency 
            and service reliability.
          </p>
        </div>
      </div>
    );
  }
);

SizingPdfReport.displayName = 'SizingPdfReport';
